import React, { useEffect, useState } from 'react';
import { AgGridReact } from 'ag-grid-react';
import { ColDef } from 'ag-grid-community';
import { themeQuartz } from 'ag-grid-community';
import '../../config/agGridConfig'; // Register AG Grid modules
import { adminAPI } from '../../services/api';
import { useDataRefresh } from '../../context/DataRefreshContext';
import { Category } from '../../types';
import CategoryFormModal from '../../components/admin/CategoryFormModal';
import ConfirmDialog from '../../components/admin/ConfirmDialog';
import Toast from '../../components/admin/Toast';
import '../../styles/AdminGlobal.css';
import './Admin.css';

const CategoryManagement: React.FC = () => {
  const [categories, setCategories] = useState<Category[]>([]);
  const [loading, setLoading] = useState(true);
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [isDeleteDialogOpen, setIsDeleteDialogOpen] = useState(false);
  const [selectedCategory, setSelectedCategory] = useState<Category | null>(null);
  const [searchTerm, setSearchTerm] = useState('');
  const { triggerRefresh } = useDataRefresh();
  const [toast, setToast] = useState<{show: boolean, message: string, type: 'success' | 'error'}>({
    show: false,
    message: '',
    type: 'success'
  });

  const columnDefs: ColDef[] = [
    { field: 'id', headerName: 'ID', width: 70, sortable: true },
    { field: 'name_en', headerName: 'Name (EN)', sortable: true, filter: true, flex: 1 },
    { field: 'name_bn', headerName: 'Name (BN)', sortable: true, filter: true, flex: 1 },
    { field: 'slug', headerName: 'Slug', sortable: true, width: 150 },
    { 
      field: 'is_active', 
      headerName: 'Active', 
      width: 100,
      cellRenderer: (params: any) => params.value ? '✓' : '✗'
    },
    { 
      headerName: 'Actions',
      width: 100,
      pinned: 'right',
      lockPosition: true,
      suppressMovable: true,
      sortable: false,
      filter: false,
      cellRenderer: (params: any) => {
        const buttonStyle = {
          border: 'none',
          borderRadius: '6px',
          width: '32px',
          height: '32px',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          cursor: 'pointer',
          fontSize: '16px',
          transition: 'all 0.2s ease',
          boxShadow: '0 2px 4px rgba(0,0,0,0.1)'
        };
        
        return (
          <div style={{ display: 'flex', gap: '6px', alignItems: 'center', justifyContent: 'center', height: '100%' }}>
            <button 
              onClick={() => handleEdit(params.data)}
              title="Edit Category"
              style={{ 
                ...buttonStyle,
                backgroundColor: '#007bff',
                color: 'white'
              }}
              onMouseEnter={(e) => (e.currentTarget.style.transform = 'scale(1.1)')}
              onMouseLeave={(e) => (e.currentTarget.style.transform = 'scale(1)')}
            >
              ✏️
            </button>
            <button 
              onClick={() => handleDeleteClick(params.data)}
              title="Delete Category"
              style={{ 
                ...buttonStyle,
                backgroundColor: '#dc3545',
                color: 'white'
              }}
              onMouseEnter={(e) => (e.currentTarget.style.transform = 'scale(1.1)')}
              onMouseLeave={(e) => (e.currentTarget.style.transform = 'scale(1)')}
            >
              🗑️
            </button>
          </div>
        );
      }
    }
  ];

  useEffect(() => {
    loadCategories();
  }, []);

  const loadCategories = async () => {
    try {
      setLoading(true);
      const response = await adminAPI.getCategories();
      console.log('Categories response:', response); // Debug log
      
      // Handle axios response structure
      let categoriesList: Category[] = [];
      if (response.data) {
        // If response.data is an array, use it directly
        if (Array.isArray(response.data)) {
          categoriesList = response.data;
        } 
        // If response.data has a results property (paginated), use that
        else if (response.data.results && Array.isArray(response.data.results)) {
          categoriesList = response.data.results;
        }
        // If response.data has a data property, use that
        else if (response.data.data && Array.isArray(response.data.data)) {
          categoriesList = response.data.data;
        }
      }
      // Fallback: if response itself is an array
      else if (Array.isArray(response)) {
        categoriesList = response;
      }
      
      console.log('Processed categories:', categoriesList); // Debug log
      setCategories(categoriesList);
    } catch (error) {
      console.error('Failed to load categories:', error);
      setToast({ show: true, message: 'Failed to load categories', type: 'error' });
      setCategories([]); // Set empty array on error
    } finally {
      setLoading(false);
    }
  };

  const handleEdit = (category: Category) => {
    setSelectedCategory(category);
    setIsModalOpen(true);
  };

  const handleDeleteClick = (category: Category) => {
    setSelectedCategory(category);
    setIsDeleteDialogOpen(true);
  };

  const handleDelete = async () => {
    if (!selectedCategory) return;
    
    try {
      await adminAPI.deleteCategory(selectedCategory.id);
      setToast({ show: true, message: 'Category deleted successfully', type: 'success' });
      loadCategories();
      // ✅ Trigger refresh for category data
      triggerRefresh('categories');
    } catch (error) {
      console.error('Delete error:', error);
      setToast({ show: true, message: 'Failed to delete category', type: 'error' });
    } finally {
      setIsDeleteDialogOpen(false);
      setSelectedCategory(null);
    }
  };

  const handleSave = async (formData: any) => {
    try {
      if (selectedCategory) {
        await adminAPI.updateCategory(selectedCategory.id, formData);
        setToast({ show: true, message: 'Category updated successfully', type: 'success' });
      } else {
        await adminAPI.createCategory(formData);
        setToast({ show: true, message: 'Category created successfully', type: 'success' });
      }
      setIsModalOpen(false);
      setSelectedCategory(null);
      loadCategories();
      // ✅ Trigger refresh for category data
      triggerRefresh('categories');
    } catch (error) {
      console.error('Save error:', error);
      throw error;
    }
  };

  const handleAddNew = () => {
    setSelectedCategory(null);
    setIsModalOpen(true);
  };

  const handleSearch = (e: React.ChangeEvent<HTMLInputElement>) => {
    setSearchTerm(e.target.value);
  };

  // Ensure categories is always an array before filtering
  const categoriesArray = Array.isArray(categories) ? categories : [];
  const filteredCategories = categoriesArray.filter(category =>
    category.name_en.toLowerCase().includes(searchTerm.toLowerCase()) ||
    category.name_bn.toLowerCase().includes(searchTerm.toLowerCase())
  );

  return (
    <div className="admin-page">
      <div className="management-header">
        <div>
          <h3>Category Management</h3>
          <p>Organize and manage product categories</p>
        </div>
        <div className="management-actions">
          <button className="btn btn-success" onClick={handleAddNew}>
            + Add New Category
          </button>
          <button className="btn btn-secondary" onClick={loadCategories}>
            🔄 Refresh
          </button>
        </div>
      </div>
      
      <div className="ag-grid-wrapper">
        <div className="search-box">
          <input
            type="text"
            placeholder="Search categories by name..."
            value={searchTerm}
            onChange={handleSearch}
          />
        </div>
        <div className="results-info">
          <span>{filteredCategories.length}</span> categories found
        </div>
      
        <div className="ag-theme-alpine ag-grid-container">
          <AgGridReact
            theme={themeQuartz}
            rowData={filteredCategories || []}
            columnDefs={columnDefs}
            pagination={true}
            paginationPageSize={20}
            loading={loading}
            defaultColDef={{
              resizable: true,
            }}
          />
        </div>
      </div>

      <CategoryFormModal
        isOpen={isModalOpen}
        onClose={() => { 
          setIsModalOpen(false); 
          setSelectedCategory(null); 
        }}
        onSave={handleSave}
        category={selectedCategory}
        categories={categories}
      />

      <ConfirmDialog
        isOpen={isDeleteDialogOpen}
        onClose={() => {
          setIsDeleteDialogOpen(false);
          setSelectedCategory(null);
        }}
        onConfirm={handleDelete}
        title="Delete Category"
        message={`Are you sure you want to delete "${selectedCategory?.name_en}"? This action cannot be undone.`}
        type="danger"
        confirmText="Delete"
      />

      {toast.show && (
        <Toast
          {...toast}
          onClose={() => setToast({ ...toast, show: false })}
        />
      )}
    </div>
  );
};

export default CategoryManagement;
